import React from 'react';

import Tooltip from 'components/common/tooltip';
import CalendarCross from 'components/common/icons';
import RemoteIcon from 'components/common/remoteIcon';

import { COLORS } from 'constants/color';
import { WORK_LOCATION } from 'constants/worklog';
import { ATTENDANCE_LOCATION_LABEL } from 'constants/attendanceStatus';
import { MY_TEAM_ATTENDANCE_FILTERS, MY_TEAM_LABELS } from 'constants/myTeam';

import { UiBuilding, UiExclamationTriangle } from '@vyaguta/vyaguta-icons/ui';

import { AttendanceSummaryItem } from './AttendanceSummaryItem';

import type { AttendanceFilter } from '../utils/attendanceFilter';

interface AttendanceFiltersProps {
  counts: Record<AttendanceFilter, number>;
  activeFilter: AttendanceFilter | null;
  onSelect: (filter: AttendanceFilter) => void;
}

/**
 * Icon, label and resting icon colour per category. Icon colours are the design's
 * palette tokens; the icon flips to white while its counter is selected.
 */
const FILTERS: Array<{
  item: AttendanceFilter;
  label: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  iconColor: string;
}> = [
  {
    item: MY_TEAM_ATTENDANCE_FILTERS.MISSING,
    label: MY_TEAM_LABELS.PENDING_ATTENDANCE,
    icon: UiExclamationTriangle,
    iconColor: COLORS.yellow80,
  },
  {
    item: MY_TEAM_ATTENDANCE_FILTERS.WFO,
    label: ATTENDANCE_LOCATION_LABEL[WORK_LOCATION.OFFICE].present,
    icon: UiBuilding,
    iconColor: COLORS.green60,
  },
  {
    item: MY_TEAM_ATTENDANCE_FILTERS.WFH,
    label: ATTENDANCE_LOCATION_LABEL[WORK_LOCATION.HOME].present,
    icon: RemoteIcon,
    iconColor: COLORS.blue60,
  },
  {
    item: MY_TEAM_ATTENDANCE_FILTERS.LEAVE,
    label: MY_TEAM_LABELS.ON_LEAVE,
    icon: CalendarCross,
    iconColor: COLORS.purple,
  },
];

/**
 * The four today's-attendance counters in the MyTeam header. Single-select: the
 * selected counter is highlighted in its own colour and clicking it again clears
 * the selection (see nextAttendanceFilter).
 */
const AttendanceFilters: React.FC<AttendanceFiltersProps> = ({ counts, activeFilter, onSelect }) => (
  <div className="attendance-summary">
    {FILTERS.map(({ item, label, icon, iconColor }) => (
      <Tooltip key={item} html={<div className="tooltip-info">{label}</div>}>
        <button
          type="button"
          aria-label={label}
          aria-pressed={activeFilter === item}
          className="button-bg-white"
          onClick={() => onSelect(item)}
        >
          <AttendanceSummaryItem
            icon={icon}
            size={16}
            count={counts[item]}
            iconColor={activeFilter === item ? COLORS.white : iconColor}
            item={item}
            attendance={activeFilter ?? ''}
          />
        </button>
      </Tooltip>
    ))}
  </div>
);

export default AttendanceFilters;
